import React from 'react';
import { createRoot } from 'react-dom/client';
import App from './App';
import './index.css';
import reportWebVitals from './reportWebVitals';

// Error Boundary Component
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({
      error: error,
      errorInfo: errorInfo
    });
    
    // Log error to monitoring service
    console.error('Error caught by boundary:', error, errorInfo);
    
    // You can send to error tracking service like Sentry
    // Sentry.captureException(error, { extra: errorInfo });
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
          <div className="max-w-md w-full">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center">
              <div className="w-16 h-16 mx-auto mb-6 bg-red-100 rounded-full flex items-center justify-center">
                <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              
              <h2 className="text-xl font-semibold text-gray-900 mb-2">
                Something went wrong
              </h2>
              
              <p className="text-gray-600 mb-6">
                We're sorry, but something unexpected happened. Please try refreshing the page.
              </p>
              
              <div className="space-y-3">
                <button
                  onClick={() => window.location.reload()}
                  className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
                >
                  Refresh Page
                </button>
                
                <button
                  onClick={() => this.setState({ hasError: false, error: null, errorInfo: null })}
                  className="w-full px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Try Again
                </button>
              </div>
              
              {process.env.NODE_ENV === 'development' && this.state.error && (
                <details className="mt-6 text-left">
                  <summary className="cursor-pointer text-sm text-gray-500 hover:text-gray-700">
                    Show Error Details
                  </summary>
                  <div className="mt-2 p-3 bg-gray-50 rounded border text-xs text-gray-600 overflow-auto">
                    <div className="font-semibold mb-2">Error:</div>
                    <pre className="whitespace-pre-wrap">{this.state.error.toString()}</pre>
                    
                    <div className="font-semibold mt-4 mb-2">Stack Trace:</div>
                    <pre className="whitespace-pre-wrap">{this.state.errorInfo.componentStack}</pre>
                  </div>
                </details>
              )}
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Performance monitoring
const measurePerformance = () => {
  if ('performance' in window) {
    window.addEventListener('load', () => {
      const perfData = performance.getEntriesByType('navigation')[0];
      if (perfData) {
        console.log('Performance Metrics:', {
          'DNS Lookup': perfData.domainLookupEnd - perfData.domainLookupStart,
          'TCP Connection': perfData.connectEnd - perfData.connectStart,
          'Request + Response': perfData.responseEnd - perfData.requestStart,
          'DOM Processing': perfData.domComplete - perfData.domLoading,
          'Total Load Time': perfData.loadEventEnd - perfData.loadEventStart
        });
      }
    });
  }
};

// Theme management
const initializeTheme = () => {
  const savedTheme = localStorage.getItem('admin-theme');
  const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
  const theme = savedTheme || (prefersDark ? 'dark' : 'light');
  
  document.documentElement.setAttribute('data-theme', theme);
  
  // Listen for system theme changes
  window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', (e) => {
    if (!localStorage.getItem('admin-theme')) {
      document.documentElement.setAttribute('data-theme', e.matches ? 'dark' : 'light');
    }
  });
};

// Feature detection
const detectFeatures = () => {
  const features = {
    localStorage: 'localStorage' in window,
    sessionStorage: 'sessionStorage' in window,
    webSockets: 'WebSocket' in window,
    geolocation: 'geolocation' in navigator,
    notifications: 'Notification' in window,
    serviceWorker: 'serviceWorker' in navigator,
    indexedDB: 'indexedDB' in window,
    webGL: (() => {
      try {
        const canvas = document.createElement('canvas');
        return !!(window.WebGLRenderingContext && canvas.getContext('webgl'));
      } catch (e) {
        return false;
      }
    })(),
    touchEvents: 'ontouchstart' in window,
    pointerEvents: 'onpointerdown' in window,
    css: {
      grid: CSS.supports('display', 'grid'),
      flexbox: CSS.supports('display', 'flex'),
      customProperties: CSS.supports('color', 'var(--test)'),
      backdropFilter: CSS.supports('backdrop-filter', 'blur(1px)')
    }
  };
  
  // Store features globally for components to use
  window.APP_FEATURES = features;
  
  // Add feature classes to body for CSS
  Object.entries(features).forEach(([key, value]) => {
    if (typeof value === 'boolean') {
      document.body.classList.add(value ? `has-${key}` : `no-${key}`);
    }
  });
  
  if (process.env.NODE_ENV === 'development') {
    console.log('Browser Features:', features);
  }
};

// Global error handlers
const setupErrorHandlers = () => {
  // Unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    console.error('Unhandled promise rejection:', event.reason);
    
    // Prevent default browser error handling
    event.preventDefault();
    
    // You can send to error tracking service
    // Sentry.captureException(event.reason);
  });

  // Global JavaScript errors
  window.addEventListener('error', (event) => {
    console.error('Global error:', {
      message: event.message,
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
      error: event.error
    });
    
    // You can send to error tracking service
    // Sentry.captureException(event.error);
  });

  // Resource loading errors
  window.addEventListener('error', (event) => {
    if (event.target !== window) {
      console.error('Resource loading error:', {
        type: event.target.tagName,
        source: event.target.src || event.target.href,
        message: 'Failed to load resource'
      });
    }
  }, true);
};

// Service Worker registration
const registerServiceWorker = () => {
  if ('serviceWorker' in navigator && process.env.NODE_ENV === 'production') {
    window.addEventListener('load', () => {
      navigator.serviceWorker.register('/sw.js')
        .then((registration) => {
          console.log('SW registered: ', registration);
          
          // Check for updates
          registration.addEventListener('updatefound', () => {
            const newWorker = registration.installing;
            if (newWorker) {
              newWorker.addEventListener('statechange', () => {
                if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                  // New content is available
                  if (window.confirm('New version available! Would you like to update?')) {
                    window.location.reload();
                  }
                }
              });
            }
          });
        })
        .catch((registrationError) => {
          console.log('SW registration failed: ', registrationError);
        });
    });
  }
};

// Network status monitoring
const monitorNetwork = () => {
  const updateNetworkStatus = () => {
    const isOnline = navigator.onLine;
    document.body.classList.toggle('offline', !isOnline);
    
    if (!isOnline) {
      console.warn('Application is offline');
      // You can show offline notification here
    } else {
      console.log('Application is online');
      // You can hide offline notification here
    }
  };

  window.addEventListener('online', updateNetworkStatus);
  window.addEventListener('offline', updateNetworkStatus);
  
  // Initial check
  updateNetworkStatus();
};

// Initialize application
const initializeApp = () => {
  // Setup global configurations
  initializeTheme();
  detectFeatures();
  setupErrorHandlers();
  measurePerformance();
  registerServiceWorker();
  monitorNetwork();
  
  // Global app configuration
  window.APP_CONFIG = {
    name: 'SecureAdmin',
    version: process.env.REACT_APP_VERSION || '1.0.0',
    environment: process.env.NODE_ENV,
    apiUrl: process.env.REACT_APP_API_URL,
    enableAnalytics: process.env.REACT_APP_ENABLE_ANALYTICS === 'true',
    enableSentry: process.env.REACT_APP_ENABLE_SENTRY === 'true',
    debugMode: process.env.NODE_ENV === 'development'
  };
  
  if (process.env.NODE_ENV === 'development') {
    console.log('App Configuration:', window.APP_CONFIG);
  }
};

// App component with providers
const AppWithProviders = () => {
  return (
    <ErrorBoundary>
      <React.StrictMode>
        <App />
      </React.StrictMode>
    </ErrorBoundary>
  );
};

// Main render function
const renderApp = () => {
  const container = document.getElementById('root');
  
  if (!container) {
    throw new Error('Root element not found');
  }
  
  const root = createRoot(container);
  
  root.render(<AppWithProviders />);
  
  // Hide loading screen after app mounts
  setTimeout(() => {
    const loadingScreen = document.getElementById('loading-screen');
    if (loadingScreen) {
      loadingScreen.style.opacity = '0';
      setTimeout(() => {
        loadingScreen.remove();
      }, 300);
    }
  }, 1000);
};

// Initialize and render
try {
  initializeApp();
  renderApp();
} catch (error) {
  console.error('Failed to initialize app:', error);
  
  // Fallback error display
  document.getElementById('root').innerHTML = `
    <div style="
      min-height: 100vh;
      display: flex;
      align-items: center;
      justify-content: center;
      background: #f9fafb;
      font-family: Inter, sans-serif;
      padding: 20px;
    ">
      <div style="
        background: white;
        padding: 40px;
        border-radius: 12px;
        box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1);
        text-align: center;
        max-width: 400px;
        width: 100%;
      ">
        <h1 style="color: #dc2626; margin-bottom: 16px; font-size: 20px;">
          Application Failed to Load
        </h1>
        <p style="color: #6b7280; margin-bottom: 24px;">
          There was an error starting the application. Please refresh the page or contact support.
        </p>
        <button 
          onclick="window.location.reload()" 
          style="
            background: #3b82f6;
            color: white;
            border: none;
            padding: 12px 24px;
            border-radius: 8px;
            cursor: pointer;
            font-weight: 500;
          "
        >
          Reload Application
        </button>
      </div>
    </div>
  `;
}

// Performance measurement and reporting
reportWebVitals((metric) => {
  if (process.env.NODE_ENV === 'development') {
    console.log('Web Vital:', metric);
  }
  
  // Send to analytics service
  if (window.APP_CONFIG?.enableAnalytics) {
    // Example: gtag('event', metric.name, { value: metric.value });
  }
});

// Expose useful globals for development
if (process.env.NODE_ENV === 'development') {
  window.debug = {
    clearStorage: () => {
      localStorage.clear();
      sessionStorage.clear();
      console.log('Storage cleared');
    },
    toggleTheme: () => {
      const current = document.documentElement.getAttribute('data-theme');
      const newTheme = current === 'dark' ? 'light' : 'dark';
      document.documentElement.setAttribute('data-theme', newTheme);
      localStorage.setItem('admin-theme', newTheme);
      console.log('Theme changed to:', newTheme);
    },
    getPerformance: () => {
      if ('performance' in window) {
        return {
          navigation: performance.getEntriesByType('navigation')[0],
          resources: performance.getEntriesByType('resource'),
          measures: performance.getEntriesByType('measure'),
          marks: performance.getEntriesByType('mark')
        };
      }
    }
  };
  
  console.log('Debug utilities available:', Object.keys(window.debug));
}